import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle, AlertCircle, Upload, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import api from '../../utils/api';

const METODOS_COBRO = [
  { value: 'transferencia', label: 'Transferencia bancaria' },
  { value: 'tarjeta', label: 'Tarjeta de crédito/débito' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'pagare', label: 'Pagaré' },
  { value: 'recibo', label: 'Recibo domiciliado' },
];

const FieldHelp = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="w-4 h-4 text-slate-400 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function BillingDataStep({ initialData, onNext, onBack, saving, tenantId }) {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(() => ({
    serie_factura: initialData?.serie_factura || '',
    numero_inicial: initialData?.numero_inicial || 1,
    fecha_inicio_numeracion: initialData?.fecha_inicio_numeracion || new Date().toISOString().split('T')[0],
    logo_empresa: initialData?.logo_empresa || null,
    direccion_factura: initialData?.direccion_factura || '',
    telefono_factura: initialData?.telefono_factura || '',
    email_factura: initialData?.email_factura || '',
    web_empresa: initialData?.web_empresa || '',
    texto_legal_factura: initialData?.texto_legal_factura || '',
    metodo_cobro_defecto: initialData?.metodo_cobro_defecto || 'transferencia',
    iban_mostrar: initialData?.iban_mostrar || '',
    dias_vencimiento_defecto: initialData?.dias_vencimiento_defecto || 30,
  }));
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(initialData?.logo_empresa || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [ibanValidation, setIbanValidation] = useState({ checked: false, valid: null });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    if (field === 'iban_mostrar') {
      setIbanValidation({ checked: false, valid: null });
    }
  };

  const validateIBAN = async () => {
    if (!formData.iban_mostrar) return;
    
    try {
      const response = await api.post('/validate/iban', { iban: formData.iban_mostrar });
      setIbanValidation({ checked: true, valid: response.data.valid });
      if (!response.data.valid) {
        setErrors(prev => ({ ...prev, iban_mostrar: response.data.error }));
      }
    } catch (error) {
      console.error('Error validating IBAN:', error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, logo_empresa: 'Tipo de archivo no permitido. Use PNG, JPG o SVG.' }));
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logo_empresa: 'El archivo excede el límite de 2MB' }));
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post(`/onboarding/${tenantId}/upload-logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      handleChange('logo_empresa', response.data.path);
      setErrors(prev => ({ ...prev, logo_empresa: null }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrors(prev => ({ ...prev, logo_empresa: 'Error al subir el logo' }));
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    handleChange('logo_empresa', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.serie_factura) {
      newErrors.serie_factura = 'La serie de factura es obligatoria';
    }
    if (!formData.numero_inicial || formData.numero_inicial < 1) {
      newErrors.numero_inicial = 'El número inicial debe ser al menos 1';
    }
    if (!formData.fecha_inicio_numeracion) {
      newErrors.fecha_inicio_numeracion = 'La fecha de inicio es obligatoria';
    }
    if (!formData.email_factura) {
      newErrors.email_factura = 'El email de facturación es obligatorio';
    } else if (!/\S+@\S+\.\S+/.test(formData.email_factura)) {
      newErrors.email_factura = 'Email inválido';
    }
    if (formData.metodo_cobro_defecto === 'transferencia' && !formData.iban_mostrar) {
      newErrors.iban_mostrar = 'El IBAN es obligatorio para pagos por transferencia';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext(formData);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Configuración de Facturación</h2>
        <p className="text-slate-600">Define cómo quieres que se generen tus facturas</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Serie de Factura */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Serie de Factura <span className="text-red-500">*</span>
            <FieldHelp text="Prefijo para la numeración de facturas. Ej: A, 2025A, FAC" />
          </Label>
          <Input
            value={formData.serie_factura}
            onChange={(e) => handleChange('serie_factura', e.target.value.toUpperCase())}
            placeholder="Ej: A, 2025A"
            maxLength={10}
            className={errors.serie_factura ? 'border-red-500' : ''}
            data-testid="input-serie-factura"
          />
          {errors.serie_factura && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.serie_factura}
            </p>
          )}
        </div>

        {/* Número Inicial */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Número Inicial <span className="text-red-500">*</span>
            <FieldHelp text="Número desde el cual comenzará la numeración" />
          </Label>
          <Input
            type="number"
            min="1"
            value={formData.numero_inicial}
            onChange={(e) => handleChange('numero_inicial', parseInt(e.target.value) || 1)}
            className={errors.numero_inicial ? 'border-red-500' : ''}
            data-testid="input-numero-inicial"
          />
          {errors.numero_inicial && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.numero_inicial}
            </p>
          )}
        </div>

        {/* Fecha Inicio Numeración */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Fecha Inicio Numeración <span className="text-red-500">*</span>
            <FieldHelp text="Fecha desde la cual es válida esta serie de numeración" />
          </Label>
          <Input
            type="date"
            value={formData.fecha_inicio_numeracion}
            onChange={(e) => handleChange('fecha_inicio_numeracion', e.target.value)}
            className={errors.fecha_inicio_numeracion ? 'border-red-500' : ''}
            data-testid="input-fecha-inicio"
          />
          {errors.fecha_inicio_numeracion && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.fecha_inicio_numeracion}
            </p>
          )}
        </div>

        {/* Días Vencimiento */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Días de Vencimiento <span className="text-red-500">*</span>
            <FieldHelp text="Días hasta vencimiento de pago (0 = contado)" />
          </Label>
          <Select 
            value={String(formData.dias_vencimiento_defecto)} 
            onValueChange={(v) => handleChange('dias_vencimiento_defecto', parseInt(v))}
          >
            <SelectTrigger data-testid="select-dias-vencimiento">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Contado (0 días)</SelectItem>
              <SelectItem value="15">15 días</SelectItem>
              <SelectItem value="30">30 días</SelectItem>
              <SelectItem value="45">45 días</SelectItem>
              <SelectItem value="60">60 días</SelectItem>
              <SelectItem value="90">90 días</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logo Empresa */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2 mb-2">
            Logo de la Empresa
            <FieldHelp text="Logo que aparecerá en tus facturas. Máx 2MB, formatos: PNG, JPG, SVG" />
          </Label>
          <div className="flex items-start gap-4">
            <div 
              className={`
                relative w-40 h-24 border-2 border-dashed rounded-lg flex items-center justify-center
                ${logoPreview ? 'border-green-300 bg-green-50' : 'border-slate-300 bg-slate-50'}
                transition-colors cursor-pointer hover:border-primary
              `}
              onClick={() => !uploadingLogo && fileInputRef.current?.click()}
            >
              {uploadingLogo ? (
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : logoPreview ? (
                <>
                  <img 
                    src={logoPreview} 
                    alt="Logo preview" 
                    className="max-w-full max-h-full object-contain p-2"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <div className="text-center text-sm text-slate-500">
                  <Upload className="w-6 h-6 mx-auto mb-1" />
                  <span>Subir logo</span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="input-logo"
            />
            <div className="text-sm text-slate-500">
              <p>Formatos: PNG, JPG, SVG</p>
              <p>Máximo: 2MB</p>
              <p>Recomendado: 400x200px</p>
            </div>
          </div>
          {errors.logo_empresa && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.logo_empresa}
            </p>
          )}
        </div>

        {/* Email Facturación */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Email de Facturación <span className="text-red-500">*</span>
            <FieldHelp text="Email donde se enviarán las facturas emitidas" />
          </Label>
          <Input
            type="email"
            value={formData.email_factura}
            onChange={(e) => handleChange('email_factura', e.target.value)}
            placeholder="facturacion@empresa.com"
            className={errors.email_factura ? 'border-red-500' : ''}
            data-testid="input-email-factura"
          />
          {errors.email_factura && (
            <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" /> {errors.email_factura}
            </p>
          )}
        </div>

        {/* Teléfono Factura */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Teléfono en Factura
          </Label>
          <Input
            value={formData.telefono_factura}
            onChange={(e) => handleChange('telefono_factura', e.target.value)}
            placeholder="+34 612 345 678"
            data-testid="input-telefono-factura"
          />
        </div>

        {/* Web Empresa */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Sitio Web
          </Label>
          <Input
            value={formData.web_empresa}
            onChange={(e) => handleChange('web_empresa', e.target.value)}
            placeholder="https://www.empresa.com"
            data-testid="input-web"
          />
        </div>

        {/* Método de Cobro */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            Método de Cobro por Defecto <span className="text-red-500">*</span>
          </Label>
          <Select 
            value={formData.metodo_cobro_defecto} 
            onValueChange={(v) => handleChange('metodo_cobro_defecto', v)}
          >
            <SelectTrigger data-testid="select-metodo-cobro">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METODOS_COBRO.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* IBAN (condicional) */}
        {formData.metodo_cobro_defecto === 'transferencia' && (
          <div className="md:col-span-2">
            <Label className="flex items-center gap-2 mb-2">
              IBAN para Transferencias <span className="text-red-500">*</span>
              <FieldHelp text="El IBAN que aparecerá en tus facturas para pagos por transferencia" />
            </Label>
            <div className="relative max-w-md">
              <Input
                value={formData.iban_mostrar}
                onChange={(e) => handleChange('iban_mostrar', e.target.value.toUpperCase().replace(/\s/g, ''))}
                onBlur={validateIBAN}
                placeholder="ES12 3456 7890 1234 5678 9012"
                className={`pr-10 ${errors.iban_mostrar ? 'border-red-500' : ibanValidation.valid ? 'border-green-500' : ''}`}
                data-testid="input-iban"
              />
              {ibanValidation.checked && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {ibanValidation.valid ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {errors.iban_mostrar && (
              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {errors.iban_mostrar}
              </p>
            )}
          </div>
        )}

        {/* Dirección en Factura */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2 mb-2">
            Dirección en Factura
            <FieldHelp text="Dirección que aparecerá en las facturas. Si se deja vacío, se usa el domicilio fiscal." />
          </Label>
          <Textarea
            value={formData.direccion_factura}
            onChange={(e) => handleChange('direccion_factura', e.target.value)}
            placeholder="Dejar vacío para usar el domicilio fiscal"
            rows={2}
            data-testid="input-direccion-factura"
          />
        </div>

        {/* Texto Legal */}
        <div className="md:col-span-2">
          <Label className="flex items-center gap-2 mb-2">
            Texto Legal en Factura
            <FieldHelp text="Texto que aparecerá al pie de las facturas (registro mercantil, etc.)" />
          </Label>
          <Textarea
            value={formData.texto_legal_factura}
            onChange={(e) => handleChange('texto_legal_factura', e.target.value)}
            placeholder="Ej: Inscrita en el Registro Mercantil de Madrid, Tomo 12345, Folio 67..."
            rows={2}
            maxLength={1000}
            data-testid="input-texto-legal"
          />
          <p className="text-xs text-slate-500 mt-1">{formData.texto_legal_factura.length}/1000 caracteres</p>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack} data-testid="btn-back">
          <ChevronLeft className="w-4 h-4 mr-2" /> Atrás
        </Button>
        <Button onClick={handleSubmit} disabled={saving || uploadingLogo} data-testid="btn-next">
          {saving ? 'Guardando...' : 'Continuar'} <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
